import { create } from 'zustand';
import { devtools, persist } from 'zustand/middleware';

/**
 * UI Mode Store - Gestion du mode Simple/Expert
 *
 * Mode Simple (Artisan solo):
 * - Interface simplifiée avec uniquement les features essentielles
 * - Dashboard, Devis, Factures, Clients, Produits, Paramètres
 *
 * Mode Expert (PME BTP):
 * - Toutes les features visibles
 * - Trésorerie, TVA, Comptabilité, CRM, ERP, IA, etc.
 *
 * IMPORTANT: Ce store utilise persist avec hydratation asynchrone.
 * Utilisez `hasHydrated` pour savoir si le store est prêt avant de rendre le menu.
 */

export type UIMode = 'simple' | 'expert';

interface UIModeState {
  mode: UIMode;
  hasHydrated: boolean;
  setMode: (mode: UIMode) => void;
  toggleMode: () => void;
  isSimpleMode: () => boolean;
  isExpertMode: () => boolean;
  setHasHydrated: (value: boolean) => void;
}

export const useUIModeStore = create<UIModeState>()(
  devtools(
    persist(
      (set, get) => ({
        // Mode par défaut: Simple (moins intimidant pour artisans solo)
        mode: 'simple',
        // État d'hydratation - false jusqu'à ce que le localStorage soit lu
        hasHydrated: false,

        setMode: (mode: UIMode) => {
          set({ mode });
        },

        toggleMode: () => {
          const currentMode = get().mode;
          set({ mode: currentMode === 'simple' ? 'expert' : 'simple' });
        },

        isSimpleMode: () => get().mode === 'simple',

        isExpertMode: () => get().mode === 'expert',

        setHasHydrated: (value: boolean) => {
          set({ hasHydrated: value });
        },
      }),
      {
        name: 'ui-mode-storage',
        version: 3, // Incrémenté pour forcer la rehydratation propre
        partialize: (state) => ({
          mode: state.mode,
          // Ne pas persister hasHydrated
        }),
        migrate: (persistedState: unknown) => {
          // Migration: garder le mode existant ou utiliser 'simple' par défaut
          const state = persistedState as { mode?: UIMode } | undefined;
          return {
            mode: state?.mode || 'simple',
          };
        },
        onRehydrateStorage: () => (state) => {
          // Appelé quand l'hydratation est terminée
          if (state) {
            state.setHasHydrated(true);
          }
        },
      }
    ),
    { name: 'UIModeStore' }
  )
);

/**
 * Hook pour attendre l'hydratation du store
 * Utiliser dans les composants qui dépendent du mode UI
 */
export const useUIModeHydration = () => {
  const hasHydrated = useUIModeStore((state) => state.hasHydrated);
  return hasHydrated;
};
